<script setup lang="ts">
import {Sharing} from "@/core/interfaces/Sharing";
import {Dialog, DialogScrollContent, DialogTitle, DialogTrigger} from "@/core/components/ui/dialog";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {computed, reactive, ref} from "vue";
import {sharingApi} from "@/container";
import {useLiveStore} from "@/stores/live.store";
import {LiveItemType} from "@/core/interfaces/live/LiveItem";
import {Textarea} from "@/core/components/ui/textarea";
import SharingService from "@/sharing/services/SharingService";
import HospitalPatientFileRow from "@/hospital/components/hospital-patient-file-row/HospitalPatientFileRow.vue";
import {Button} from "@/core/components/ui/button";
import {useRouter} from "vue-router";

const liveStore = useLiveStore();
const {sharing, showSeeData = true} = defineProps<{
    sharing: Sharing;
    showSeeData: boolean
}>();
const loadings = reactive({
    accept: false,
    refuse: false,
});
const open = ref(false);
const comment = ref();
const router = useRouter();
const emits = defineEmits<{
    (e: 'handled'): void;
}>();

/**
 * Accept the sharing request.
 */
const accept = () => {
    loadings.accept = true;

    sharingApi()
        .accept(sharing.id, comment.value)
        .then(removeFromStore)
        .finally(() => {
            loadings.accept = false;
            emits('handled');
            open.value = false;
        });
};

/**
 * Refuse the sharing request.
 */
const refuse = () => {
    loadings.refuse = true;

    sharingApi()
        .decline(sharing.id, comment.value)
        .then(removeFromStore)
        .finally(() => {
            loadings.refuse = false;
            emits('handled');
            open.value = false;
        });
};

/**
 * Remove the sharing item from the live store.
 */
const removeFromStore = () => {
    liveStore.deleteItems(sharing, LiveItemType.SHARING);
};

/**
 * Get the sharing application information, including his role, and RPPS if available.
 */
const applicantInformation = computed(() => {
    let information = "";
    const applicantRole = SharingService.applicantRole(sharing);

    if (applicantRole) {
        information += `(${applicantRole.role.name}`;

        if (applicantRole.prescriptor) {
            information += `, RPPS: ${applicantRole.prescriptor.rpps}`;
        }

        information += ")";
    }

    return information;
});

const prescriptions = () => {
    const isBeneficiary = sharing.sharable_type === 'beneficiary';
    const isCustomer = sharing.sharable_type === 'customer';

    if (!isBeneficiary && !isCustomer) {
        return;
    }

    const customerId = sharing.sharable.customer_id;

    return router.push({
        name: 'customer.prescriptions',
        params: {
            customerId
        }
    });
};

</script>

<template>
  <Dialog v-model:open="open">
    <DialogTrigger as-child>
      <slot />
    </DialogTrigger>
    <DialogScrollContent>
      <DialogTitle>
        Demande de conciliation médicamenteuse
      </DialogTitle>
      <div class="flex flex-col gap-4">
        <p>Partagez les données du patient avec <strong>{{ sharing.tenant_name }}</strong></p>
        <p>
          {{ sharing.applicant.first_name }} {{ sharing.applicant.last_name }} {{ applicantInformation }} souhaite
          accéder aux ordonnances du patient <strong>{{ sharing.sharable.information.name }}</strong> pour effectuer sa
          conciliation médicamenteuse.
        </p>
        <Button v-if="showSeeData && sharing.sharable_type === 'customer'" variant="link" class="text-primary self-center" @click="prescriptions">
          Voir les données partagées
        </Button>
        <div v-if="sharing.hospital_patient_files?.length" class="self-start space-y-2">
          <p>Fichier(s) :</p>
          <HospitalPatientFileRow
            v-for="file in sharing.hospital_patient_files"
            :key="file.id"
            :file="file"
          />
        </div>
      </div>
      <div>
        <label for="comment">Information complémentaire :</label>
        <Textarea id="comment" v-model:model-value="comment" class="min-h-8" />
      </div>
      <div class="flex justify-end gap-2">
        <BaseButton
          :loading="loadings.refuse"
          :disabled="loadings.accept || loadings.refuse"
          @click="refuse"
        >
          Refuser
        </BaseButton>
        <BaseButton
          primary
          :loading="loadings.accept"
          :disabled="loadings.accept || loadings.refuse"
          @click="accept"
        >
          Accepter
        </BaseButton>
      </div>
    </DialogScrollContent>
  </Dialog>
</template>

<style scoped>

</style>
